import React, { useState, useEffect } from 'react';
import {useScrollAnimation} from './hooks/useScrollAnimation';
import useParallax from './hooks/useParallax';
import useScrollFade from './hooks/useScrollFade';
import useHangingParallax from './hooks/useHangingParallax';
import { getProjectsByType } from './services/projectService';
import Loader from './components/Loader';
import './animations.css';
import { Link }  from 'react-router-dom'


export default function Landing() {
  // Hero images array
  const heroImages = [
    {
      src: process.env.PUBLIC_URL + '/Project2.png',
      alt: 'Commercial Project',
      title: 'COMMERCIAL INNOVATION'
    },
    {
      src: process.env.PUBLIC_URL + '/project1.png',
      alt: 'Modern Meeting Room',
      title: 'MEETING ROOM DESIGN'
    },
    {
      src: process.env.PUBLIC_URL + '/p1.jpg',
      alt: 'Modern Conference Room',
      title: 'CONFERENCE EXCELLENCE'
    },
    {
      src: process.env.PUBLIC_URL + '/DSC00365.JPG',
      alt: 'Office Corridor',
      title: 'CORRIDOR DESIGN'
    },
    {
      src: process.env.PUBLIC_URL + '/p2.jpg',
      alt: 'Office Entrance Design',
      title: 'ENTRANCE DESIGN'
    },
    {
      src: process.env.PUBLIC_URL + '/DSC00378.JPG',
      alt: 'Office Corridor',
      title: 'CORRIDOR DESIGN'
    },
    {
      src: process.env.PUBLIC_URL + '/p3.jpg',
      alt: 'Office Corridor',
      title: 'CORRIDOR DESIGN'
    },
    {
      src: process.env.PUBLIC_URL + '/DSC00345.JPG',
      alt: 'Office Corridor',
      title: 'CORRIDOR DESIGN'
    }
  ];

  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroVisible, setHeroVisible] = useState(false);
  const [imageWidth, setImageWidth] = useState(700);
  
  // Responsive image width for larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1440) {
        setImageWidth(900);
      } else {
        setImageWidth(700);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // For seamless infinite carousel, we use 3 sets of images
  const extendedHeroImages = [...heroImages, ...heroImages, ...heroImages];
  
  // For 1.5 images per view, scroll by full image width
  const IMAGE_WIDTH = imageWidth;
  const totalImages = extendedHeroImages.length;
  
  // Remove complex JS loop logic in favor of smooth CSS animation

  // Touch handlers and auto-scroll effects removed for CSS marquee

  
  const [studioRef, studioVisible] = useScrollAnimation(0.05);
  const [, p1Visible] = useScrollAnimation(0.05);
  const [, p2Visible] = useScrollAnimation(0.05);
  const [, p3Visible] = useScrollAnimation(0.05);
  const [, p4Visible] = useScrollAnimation(0.05);
  const [storyRef, storyVisible] = useScrollAnimation(0.01, { rootMargin: '400px 0px -50px 0px' }); // Extremely early trigger - 400px before visible
  // Hanging effect for Our Story image (top-right pivot)
  const hangingParallax = useHangingParallax({ rotate: { maxDeg: 35 }, translateY: { max: 60 }, skew: { maxDeg: 8 }, scale: { min: 0.97, max: 1.04 }, ease: 0.07 });
  const [servicesRef, servicesVisible] = useScrollAnimation(0.05);
  const [founderRef, founderVisible] = useScrollAnimation(0.2);
  const [testimonialRef, testimonialVisible] = useScrollAnimation(0.05);
  const [ctaRef, ctaVisible] = useScrollAnimation(0.05);
  const [ctaInnerRef, ctaInnerStyle] = useScrollFade({ inOffset: 0.25, outOffset: 0.25, translate: 28 });
  
  // Multi-layer parallax for project grid (creates sophisticated depth) - Enhanced
  const [p1ParallaxRef, p1ParallaxStyle] = useParallax({ strength: 0.3, max: 36 }); // Increased for more visibility
  const [p2ParallaxRef, p2ParallaxStyle] = useParallax({ strength: 0.7, max: 56 }); // Increased
  const [p3ParallaxRef, p3ParallaxStyle] = useParallax({ strength: -0.5, max: 44 }); // Increased
  const [p4ParallaxRef, p4ParallaxStyle] = useParallax({ strength: 1.1, max: 80 }); // Increased
  
  // Hero image parallax for enhanced depth
  const [heroImageParallaxRef, heroImageParallaxStyle] = useParallax({ strength: 0.6, max: 50 }); // Increased
  
  // Testimonial image parallax
  const [testimonialImageParallaxRef, testimonialImageParallaxStyle] = useParallax({ strength: 0.4, max: 40 });
  
  // Project section background parallax for added depth
  const [projectSectionParallaxRef, projectSectionParallaxStyle] = useParallax({ strength: -0.2, max: 30 });
  
  // Make hero visible immediately after component loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setHeroVisible(true);
    }, 100); // Very fast initial animation
    return () => clearTimeout(timer);
  }, []);
  

  
  // Load featured project from Firebase
  useEffect(() => {
    async function loadProjects() {
      try {
        setLoading(true);
        const commercialProjects = await getProjectsByType('commercial');
        const residentialProjects = await getProjectsByType('residential');
        const featuredCommercialProjects = commercialProjects.filter(project => project.isFeatured);
        const featuredResidentialProjects = residentialProjects.filter(project => project.isFeatured);
        const allLoadedProjects = [...featuredCommercialProjects, ...featuredResidentialProjects];
        
        setAllProjects(allLoadedProjects);
        setLoading(false);
      } catch (error) {
        console.error('Error loading projects:', error);
        setLoading(false);
      }
    }
    loadProjects();
  }, []);

  const featuredCommercial = allProjects.filter(p => p.type === 'commercial');
  const featuredResidential = allProjects.filter(p => p.type === 'residential');
  
  return (
    <main className="landing-page" style={{ background: '#fff', minHeight: '100vh' }}>
      {/* Hero */}
      <section className="btw-hero" style={{ background: '#fff', color: '#baa170', position: 'relative' }}>
        <div className="btw-hero-content" style={{paddingTop: '48px'}}>
          <h2 className={`btw-hero-title hero-title-animate ${heroVisible ? 'visible' : ''}`}>Transforming Spaces, Since 2009</h2>
        </div>
        <hr className="btw-hero-divider" style={{width: '94vw', maxWidth: '1280px', margin: '10px auto 32px auto', border: 'none', borderTop: '1.5px solid #baa170', background: 'transparent'}} />
        <div style={{position: 'relative', width: '100%', margin: '0 auto'}}>
          {/* CSS Marquee Slider (No Buttons, Continuous) */}
          <div className="btw-hero-image" ref={heroImageParallaxRef} style={{...heroImageParallaxStyle, boxShadow: 'none', background: 'none', position: 'relative'}}>
            <div 
              className="hero-slider-container"
              style={{position: 'relative', width: '100%', margin: '0 auto', overflow: 'hidden'}}
            >
              <div className="hero-slider-track">
                {extendedHeroImages.map((image, index) => (
                  <img
                    key={index}
                    className={`fade-in-scale ${heroVisible ? 'visible' : ''}`}
                    src={image.src}
                    alt={image.alt}
                    style={{ 
                      width: IMAGE_WIDTH, 
                      height: '100%', 
                      maxHeight: imageWidth >= 900 ? '600px' : '420px', 
                      objectFit: 'cover', 
                      borderRadius: '12px', 
                      boxShadow: 'none', 
                      background: 'none', 
                      marginLeft: '0px', 
                      marginRight: '20px',
                      flexShrink: 0
                    }}
                  />
                ))}
              </div>
            </div>
            {/* Marquee Styles */}
            <style>{`
              @keyframes slide-infinite {
                0% { transform: translateX(0); }
                100% { transform: translateX(-33.333333%); }
              }
              .hero-slider-track {
                display: flex;
                flex-direction: row;
                width: ${totalImages * (IMAGE_WIDTH + 20)}px;
                height: 420px;
                animation: slide-infinite 40s linear infinite;
              }
              .hero-slider-track:hover {
                animation-play-state: paused;
              }
              @media (min-width: 1440px) {
                .hero-slider-track {
                  height: 600px !important;
                }
              }
              @media (max-width: 768px) {
                .hero-slider-container {
                  max-width: 100vw !important;
                }
                .hero-slider-track {
                  width: ${totalImages * 100}vw !important;
                }
                .hero-slider-track img {
                  width: 100vw !important;
                  height: auto !important;
                  object-fit: contain !important;
                  margin: 0 !important;
                }
              }
            `}</style>
          </div>
        </div>
      </section>

      {/* Studio */}
      <section className="landing-studio" ref={studioRef}>
        <div className={`landing-studio-subtitle subtitle-animate ${studioVisible ? 'visible' : ''}`}>OUR STUDIO</div>
        <div className="landing-studio-content">
          <p className={`description-animate ${studioVisible ? 'visible' : ''}`}>
            Every project we create is more than just design - it’s a reflection of lifestyle, purpose, and detail. At Between the Walls, we merge creativity with functionality to bring timeless interiors to life. From homes that radiate warmth to commercial spaces that inspire productivity, each project carries our signature touch while being truly unique to the client.
          </p>
          <Link to="/Studio" style={{textDecoration: 'none'}}>
            <button className={`landing-studio-btn fade-in-up ${studioVisible ? 'visible' : ''}`}><span>Read More</span></button>
          </Link>
        </div>
      </section>

      {/* Projects Section Label */}
      <div className="landing-projects-subtitle" style={{fontFamily: 'instrument serif, serif', fontSize: '1.5rem', color: '#181816', textAlign: 'left', maxWidth: '1280px', width: '94vw', margin: '0 auto 16px auto', paddingLeft: '0', boxSizing: 'border-box'}}>FEATURED PROJECTS</div>
      <hr className="btw-project-divider" style={{width: '94vw', maxWidth: '1280px', margin: '0 auto 24px auto', border: 'none', borderTop: '1.5px solid #baa170', background: 'transparent'}} />
      {/* Projects Section */}
      <section className="btw-project-stack" ref={projectSectionParallaxRef} style={{...projectSectionParallaxStyle, paddingTop: '0px', position: 'relative', zIndex: 50}}>
        <style>{`
          @media (max-width: 768px) {
            .landing-projects-subtitle {
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding: 0 12px !important;
              text-align: left !important;
              box-sizing: border-box !important;
            }
          }
        `}</style>
        {loading ? (
          <Loader type="projects" />
        ) : (
          <>
            {/* Featured Commercial Project from Firebase */}
            {featuredCommercial[0] && (
              <div className="btw-project" style={{borderTop: '1px solid #baa170'}}>
                <div className={`btw-project-header project-header-animate ${p1Visible ? 'visible' : ''}`}> 
                  <div className="btw-project-name">{featuredCommercial[0].name.toUpperCase()}</div>
                  <div className="btw-project-type">{featuredCommercial[0].type.toUpperCase()}</div>
                </div>
                <div className={`btw-project-image project-image-animate ${p1Visible ? 'visible' : ''}`} ref={p1ParallaxRef} style={{...p1ParallaxStyle, boxShadow: 'none', background: 'none'}}>
                  <Link to={`/project/${featuredCommercial[0].id}`}>
                    <img 
                      src={featuredCommercial[0].mainImage} 
                      alt={featuredCommercial[0].name}
                      loading="eager"
                      style={{boxShadow: 'none', background: 'none'}}
                    />
                  </Link>
                </div>
              </div>
            )}
        
            {/* Featured Residential Project from Firebase */}
            {featuredResidential[0] && (
              <div className="btw-project" style={{borderTop: '1px solid #baa170'}}>
                <div className={`btw-project-header project-header-animate ${p2Visible ? 'visible' : ''}`}>
                  <div className="btw-project-name">{featuredResidential[0].name.toUpperCase()}</div>
                  <div className="btw-project-type">{featuredResidential[0].type.toUpperCase()}</div>
                </div>
                <div className={`btw-project-image project-image-animate ${p2Visible ? 'visible' : ''}`} ref={p2ParallaxRef} style={{...p2ParallaxStyle, boxShadow: 'none', background: 'none'}}>
                  <Link to={`/project/${featuredResidential[0].id}`}>
                    <img src={featuredResidential[0].mainImage} alt={featuredResidential[0].name} style={{boxShadow: 'none', background: 'none'}} />
                  </Link>
                </div>
              </div>
            )}
        
            {/* Second Commercial Project from Firebase */}
            {featuredCommercial.length > 1 && (
              <div className="btw-project" style={{borderTop: '1px solid #baa170'}}>
                <div className={`btw-project-header project-header-animate ${p3Visible ? 'visible' : ''}`}>
                  <div className="btw-project-name">{featuredCommercial[1].name.toUpperCase()}</div>
                  <div className="btw-project-type">{featuredCommercial[1].type.toUpperCase()}</div>
                </div>
                <div className={`btw-project-image project-image-animate ${p3Visible ? 'visible' : ''}`} ref={p3ParallaxRef} style={{...p3ParallaxStyle, boxShadow: 'none', background: 'none'}}>
                  <Link to={`/project/${featuredCommercial[1].id}`}>
                    <img src={featuredCommercial[1].mainImage} alt={featuredCommercial[1].name} style={{boxShadow: 'none', background: 'none'}} />
                  </Link>
                </div>
              </div>
            )}
        
            {/* Second Residential Project from Firebase */}
            {featuredResidential.length > 1 && (
              <div className="btw-project" style={{borderTop: '1px solid #baa170'}}>
                <div className={`btw-project-header project-header-animate ${p4Visible ? 'visible' : ''}`}>
                  <div className="btw-project-name">{featuredResidential[1].name.toUpperCase()}</div>
                  <div className="btw-project-type">{featuredResidential[1].type.toUpperCase()}</div>
                </div>
                <div className={`btw-project-image project-image-animate ${p4Visible ? 'visible' : ''}`} ref={p4ParallaxRef} style={{...p4ParallaxStyle, boxShadow: 'none', background: 'none'}}>
                  <Link to={`/project/${featuredResidential[1].id}`}>
                    <img src={featuredResidential[1].mainImage} alt={featuredResidential[1].name} style={{boxShadow: 'none', background: 'none'}} />
                  </Link>
                </div>
              </div>
            )}
            <div className="btw-project-footer" style={{marginTop: '0px'}}>
          <Link to="/commercial" style={{textDecoration: 'none'}}>
            <button className="btw-view-projects-btn">
              <span>View All Projects</span>
            </button>
          </Link>
        </div>
        </>
        )}
      </section>



      {/* Founder's Note Section */}
      <section className="whatwedo-sustainability-section" ref={founderRef} style={{margin: '0px auto 0 auto'}}>
        <div className="whatwedo-sustainability-inner" style={{maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'stretch', gap: window.innerWidth < 768 ? '0px' : '32px', flexDirection: window.innerWidth < 768 ? 'column-reverse' : 'row', justifyContent: 'center', padding: window.innerWidth < 768 ? '0 12px' : '0'}}>
          <div className="whatwedo-sustainability-left" style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0px', height: '100%', marginBottom: window.innerWidth < 768 ? '24px' : '0'}}>
            <div className="whatwedo-sustainability-top">
              <div className={`whatwedo-sustainability-label subtitle-animate ${founderVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem', color: '#baa170'}}>Founder's Note</div>
              <div className={`whatwedo-sustainability-title hero-title-animate ${founderVisible ? 'visible' : ''}`} style={{color: '#181816', fontWeight: 500, fontSize: '1.5rem', fontFamily: 'instrument serif, serif'}}>Evolution starts with design</div>
            </div>
            <div className="whatwedo-sustainability-desc-author-box" style={{ fontSize: '1.05rem', color: '#181816', }}>
              "Every transformation begins with a vision-design is the catalyst that turns ideas into progress. It shapes how we live, work, and interact, laying the foundation for innovation and meaningful change. When design leads, evolution follows, creating solutions that are smarter, more human-centered, and built for the future."
              <div className="whatwedo-sustainability-author" style={{marginTop: '12px', color: '#baa170', fontWeight: 500}}>Abhishek Dave @ Between The Walls, Pune</div>
            </div>
          </div>
          <div className="whatwedo-sustainability-right" style={{display: 'flex', justifyContent: 'center', width: window.innerWidth < 768 ? '100%' : 'auto'}}>
            <img src={process.env.PUBLIC_URL + '/Abhishek Dave .jpg'} alt="Sustainability" style={{height: '400px', width: 'auto', borderRadius: '8px'}} />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="btw-story-hero" ref={storyRef}>
        <div className="btw-story-content">
          <div className={`btw-story-subtitle slide-in-left ${storyVisible ? 'visible' : ''}`}>OUR STORY</div>
          <h1 className={`btw-story-title story-title-dramatic ${storyVisible ? 'visible' : ''}`}>
            DESIGNING SPACES THAT INSPIRE
          </h1>
          <button
            className="btw-story-btn-static"
            style={{
              position: 'relative',
              margin: '0px 0 0 0',
              zIndex: storyVisible ? 30 : 0, // Higher z-index during parallax effect
            }}
          >
            <Link to="/whatwedo">
              <span>Read Our Story</span>
            </Link>
          </button>
          <div
            className="btw-story-image-wrapper hanging-image"
            ref={hangingParallax.ref}
            style={{
              ...hangingParallax.style,
              position: 'relative',
              boxShadow: 'none',
              background: 'none',
              marginTop: '0px',
            }}
          >
            <img
              src={process.env.PUBLIC_URL + '/team.png'}
              alt="Our Story"
              className={`btw-story-image zoom-in-dramatic ${storyVisible ? 'visible' : ''}`}
              style={{
                boxShadow: 'none',
                background: 'transparent',
                marginTop: '0px',
                zIndex: 20,
                position: 'relative',
              }}
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="btw-services-section" ref={servicesRef}>
        <hr className="btw-service-divider" />
        <div className={`btw-service-block fade-in-up ${servicesVisible ? 'visible' : ''}`}>
          <div className="btw-service-left">
              <script src="https://cdn.lordicon.com/lordicon.js"></script>
<lord-icon
    className="btw-service-icon"
    src="https://cdn.lordicon.com/jeuxydnh.json"
    trigger="loop"
    delay="1000"
    state="morph-mantion"
    colors="primary:#baa170,secondary:#121331"
    style={{width:'75px',height:'75px'}}>
</lord-icon>
              <div className="btw-service-title">Residential</div>
            </div>
              <div className={`btw-service-right description-animate ${servicesVisible ? 'visible' : ''}`} style={{textAlign: 'left'}}>
            
            Crafting bespoke, sustainable homes that reflect individual lifestyles. Our designs combine eco-conscious materials with timeless aesthetics to create living spaces built for comfort and harmony.
          </div>
        </div>
        <hr className="btw-service-divider" />
        <div className={`btw-service-block fade-in-up ${servicesVisible ? 'visible' : ''}`}>
          <div className="btw-service-left">
              <div>
<lord-icon
    className="btw-service-icon"
    src="https://cdn.lordicon.com/tjjwskjx.json"
    trigger="loop"
    delay="2000"
    colors="primary:#baa170,secondary:#121331"
    style={{width:'75px',height:'75px'}}>
</lord-icon></div>
              <div className="btw-service-title">Commercial</div>
            </div>
              <div className={`btw-service-right description-animate ${servicesVisible ? 'visible' : ''}`} style={{textAlign: 'left'}}>
            Offices, studios, and workspaces should inspire productivity and reflect brand values. We design commercial spaces that are functional yet sophisticated - environments where teams thrive, clients feel welcome, and every corner communicates identity.
          </div>
        </div>
        <hr className="btw-service-divider" />
        <div className={`btw-service-block fade-in-up ${servicesVisible ? 'visible' : ''}`}>
          <div className="btw-service-left">
              <script src="https://cdn.lordicon.com/lordicon.js"></script>
              
<lord-icon
    className="btw-service-icon"
    src="https://cdn.lordicon.com/ehcxqqor.json"
    trigger="loop"
    delay="2000"
    colors="primary:#baa170,secondary:#121331"
    style={{width:'75px',height:'75px'}} >
</lord-icon>
              <div className="btw-service-title">Lifestyle</div>
              </div>
              <div className={`btw-service-right description-animate ${servicesVisible ? 'visible' : ''}`} style={{textAlign: 'left'}}>
            From boutique cafes to luxury retail stores, we design spaces that leave a lasting impression. Our approach balances brand storytelling with memorable customer experiences, ensuring that each project stands out in its industry.
          </div>
        </div>
        <hr className="btw-service-divider" />
        <div className={`btw-service-block fade-in-up ${servicesVisible ? 'visible' : ''}`}>
          <div className="btw-service-left">
              <lord-icon
    className="btw-service-icon"
    src="https://cdn.lordicon.com/jkpegboq.json"
    trigger="loop"
    delay="2000"
    colors="primary:#baa170,secondary:#121331"
    style={{width:'75px',height:'75px'}}>
</lord-icon>
              <div className="btw-service-title">Turnkey</div>
            </div>
              <div className={`btw-service-right description-animate ${servicesVisible ? 'visible' : ''}`} style={{textAlign: 'left'}}>
            We manage every detail of the design journey - from concept to execution - so you can enjoy a seamless experience. Our turnkey solutions cover planning, materials, decor, and handover, ensuring quality and consistency at every stage.
          </div>
        </div>
        <style>{`
          .btw-service-block {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            gap: 32px;
          }
          .btw-service-left {
            min-width: 180px;
            flex-shrink: 0;
          }
          .btw-service-right {
            flex: 1;
            min-width: 0;
          }
          @media (max-width: 768px) {
            .btw-service-block {
              flex-direction: column !important;
              gap: 12px !important;
            }
            .btw-service-left {
              width: 100% !important;
              min-width: auto !important;
              margin-bottom: 8px !important;
              flex-shrink: 1 !important;
            }
            .btw-service-right {
              width: 100% !important;
              margin-left: 0 !important;
              min-width: auto !important;
            }
          }
        `}</style>
        <div style={{paddingBottom: '48px'}}></div>
  </section>

      {/* Testimonial */}
      <section className="btw-testimonial-section bg-neutral-900 py-12 flex justify-center items-center" ref={testimonialRef}>
          <div style={{width: '94vw', maxWidth: '1280px', margin: '0 auto', backgroundColor: 'none', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', overflow: 'hidden', padding: '40px'}} className="w-full rounded-xl overflow-hidden btw-testimonial-container">
            <img className={`fade-in-scale ${testimonialVisible ? 'visible' : ''}`} ref={testimonialImageParallaxRef} src={process.env.PUBLIC_URL + '/Project2.png'} alt="Testimonial" style={{...testimonialImageParallaxStyle}} />
            <div className="btw-testimonial-content-wrapper">
              <script src="https://cdn.lordicon.com/lordicon.js"></script>
              
<lord-icon
    src="https://cdn.lordicon.com/fozsorqm.json"
    trigger="loop"
    delay="2000"
    colors="primary:#121331,secondary:#baa170"
    style={{width: '75px', height: '75px'}}>
</lord-icon>
              <div style={{paddingLeft: '4px',fontSize:'1.1rem', marginTop: '4px',marginBottom: '12px', color: '#baa170', fontWeight: 500, fontFamily: 'Inter, sans-serif'}}>Testimonial</div>
              <div className="btw-testimonial-content">
                <p className={`btw-testimonial-text description-animate ${testimonialVisible ? 'visible' : ''}`} style={{margin: 0, color: '#181616', lineHeight: 1.6}}>“Between the Walls delivered an exceptional mixed-use development that perfectly balances commercial viability with environmental sustainability. The innovative design has created a thriving community space that exceeds all our expectations for modern urban development.”</p>
                <div className={`btw-testimonial-author fade-in-up ${testimonialVisible ? 'visible' : ''}`} style={{marginTop: '16px', color: '#baa170'}}>Swapnil Gaikwad @ Phibridge</div>
              </div>
            </div>
          </div>
          <style>{`
            @media (min-width: 769px) {
              .btw-testimonial-container {
                display: flex !important;
                flex-direction: row !important;
                align-items: flex-start !important;
                width: 94vw !important;
                max-width: 1280px !important;
                padding: 40px !important;
                box-sizing: border-box !important;
                gap: 40px !important;
                margin: 0 auto !important;
              }
              .btw-testimonial-container img {
                width: 45% !important;
                max-width: 45% !important;
                aspect-ratio: 1 / 1 !important;
                height: auto !important;
                margin: 0 !important;
                display: block !important;
                flex-shrink: 0 !important;
                border-radius: 8px
              }
              .btw-testimonial-content-wrapper {
                width: 55% !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: flex-start !important;
                padding: 0 !important;
                margin-top: 0 !important;
              }
              .btw-testimonial-content {
                width: 100% !important;
                padding: 0 !important;
                box-sizing: border-box !important;
                text-align: left !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: flex-start !important;
                margin-top: 0 !important;
              }
              .btw-testimonial-text {
                color: #181616 !important;
                text-align: left !important;
                margin-bottom: 16px !important;
              }
              .btw-testimonial-author {
                color: #baa170 !important;
                text-align: left !important;
                margin-top: 0 !important;
              }
            }
            @media (max-width: 768px) {
              .btw-testimonial-container {
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                width: 100vw !important;
                max-width: 100vw !important;
                padding: 18px 8px 24px 8px !important;
                box-sizing: border-box !important;
                gap: 0 !important;
              }
              .btw-testimonial-container img {
                width: 100% !important;
                max-width: 100% !important;
                aspect-ratio: 1 / 1 !important;
                height: auto !important;
                margin: 0 0 32px 0 !important;
                display: block !important;
                border-radius: 8px
              }
              .btw-testimonial-content-wrapper {
                width: 100% !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                padding: 0 !important;
                margin-top: 8px !important;
              }
              .btw-testimonial-content {
                width: 100% !important;
                padding: 20px 12px !important;
                box-sizing: border-box !important;
                text-align: center !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                margin-top: 12px !important;
              }
              .btw-testimonial-text {
                color: #181616 !important;
                text-align: center !important;
                margin-bottom: 8px !important;
              }
              .btw-testimonial-author {
                color: #baa170 !important;
                text-align: center !important;
                margin-top: 8px !important;
              }
            }
          `}</style>
          </section>

      {/* Get Started Section */}
      <section className="btw-getstarted-section bg-neutral-900 min-h-[60vh] flex flex-col justify-center items-center text-center" ref={ctaRef}>
        <div className="btw-getstarted-inner" ref={ctaInnerRef} style={ctaInnerStyle}>
          <div className={`text-neutral-400 font-mono mb-6 subtitle-animate ${ctaVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem'}}>GET  STARTED</div>
          <h1 className={`text-5xl md:text-7xl font-bold text-[#181816] mb-8 leading-tight hero-title-animate ${ctaVisible ? 'visible' : ''}`}>TRANSFORM YOUR<br />VISION INTO REALITY</h1>
          <Link to="/letstalk" style={{textDecoration: 'none'}}>
            <button className={`btw-cta fade-in-up ${ctaVisible ? 'visible' : ''}`} type="button"><span>LET'S TALK</span></button>
          </Link>
        </div>
      </section>
    </main>
  );
}
